/**
 * Gjenbrukbar GPS-hook: watch + poll-fallback, glatting og nøyaktighetsvurdering.
 * Returnerer siste fix (lat/lon/fart/kurs/nøyaktighet) og status. Håndterer også
 * wake lock så skjermen holdes våken mens sporing pågår.
 */
import { useEffect, useRef, useState } from 'react';
import {
  metersBetween,
  bearingDeg,
  msToKnots,
  smoothAngle,
  ema,
  type AngleVec,
  type LatLon,
} from './geo.ts';
import { enableWakeLock, disableWakeLock, isWakeLockActive } from './wakelock.ts';

export interface GpsFix {
  lat: number;
  lon: number;
  sogKn: number;
  cog: number | null;
  accuracy: number | null;
  recordedAt: string; // ISO
}

export type GpsStatus = 'idle' | 'active' | 'denied' | 'unavailable';

const MIN_SPEED_MS = 0.25; // ~0.5 knop — under dette er kurs udefinert
export const GOOD_ACCURACY_M = 75; // dårligere = grov nettverksposisjon, ikke GPS

function errLabel(code: number): string {
  if (code === 1) return 'tilgang avslått';
  if (code === 2) return 'posisjon utilgjengelig';
  if (code === 3) return 'tidsavbrudd';
  return `feil ${code}`;
}

export function useGps() {
  const [status, setStatus] = useState<GpsStatus>('idle');
  const [fix, setFix] = useState<GpsFix | null>(null);
  const [searching, setSearching] = useState(false);
  const [lastEvent, setLastEvent] = useState('');
  const [permState, setPermState] = useState('ukjent');
  const [wakeLockOn, setWakeLockOn] = useState(false);

  const watchId = useRef<number | null>(null);
  const pollId = useRef<number | null>(null);
  const prevFix = useRef<(LatLon & { t: number }) | null>(null);
  const sogEma = useRef<number | null>(null);
  const cogVec = useRef<AngleVec | null>(null);

  function handle(pos: GeolocationPosition) {
    const t = pos.timestamp;
    if (prevFix.current && t === prevFix.current.t) return; // dedup poll/watch
    setSearching(false);
    const { latitude, longitude, speed, heading, accuracy } = pos.coords;
    const here: LatLon = { lat: latitude, lon: longitude };

    let speedMs: number | null =
      speed != null && Number.isFinite(speed) && speed >= 0 ? speed : null;
    if (speedMs === null && prevFix.current) {
      const dt = (t - prevFix.current.t) / 1000;
      if (dt > 0.3) speedMs = metersBetween(prevFix.current, here) / dt;
    }
    if (speedMs === null) speedMs = 0;
    const smoothedMs = ema(sogEma.current, speedMs, 0.4);
    sogEma.current = smoothedMs;

    let cog: number | null = null;
    if (smoothedMs >= MIN_SPEED_MS) {
      let course: number | null = heading != null && Number.isFinite(heading) ? heading : null;
      if (course === null && prevFix.current) {
        const moved = metersBetween(prevFix.current, here);
        if (moved > Math.max(2, (accuracy ?? 0) * 0.5)) course = bearingDeg(prevFix.current, here);
      }
      if (course !== null) {
        const r = smoothAngle(cogVec.current, course, 0.35);
        cogVec.current = r.vec;
        cog = r.angle;
      } else if (cogVec.current) {
        cog = (Math.atan2(cogVec.current.y, cogVec.current.x) * 180) / Math.PI;
        cog = (cog + 360) % 360;
      }
    } else {
      cogVec.current = null;
    }

    prevFix.current = { ...here, t };
    setFix({
      lat: latitude,
      lon: longitude,
      sogKn: Math.max(0, msToKnots(smoothedMs)),
      cog,
      accuracy: accuracy ?? null,
      recordedAt: new Date(t).toISOString(),
    });
    setLastEvent(`fix ±${Math.round(accuracy)} m`);
  }

  async function queryPermission() {
    try {
      const perms = (navigator as unknown as {
        permissions?: { query: (d: { name: PermissionName }) => Promise<PermissionStatus> };
      }).permissions;
      if (perms?.query) {
        const p = await perms.query({ name: 'geolocation' as PermissionName });
        setPermState(p.state);
        p.onchange = () => setPermState(p.state);
      }
    } catch {
      /* ignore */
    }
  }

  function stop() {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    if (pollId.current !== null) {
      clearInterval(pollId.current);
      pollId.current = null;
    }
    prevFix.current = null;
    sogEma.current = null;
    cogVec.current = null;
    void disableWakeLock();
    setWakeLockOn(false);
    setStatus('idle');
    setSearching(false);
    setFix(null);
  }

  function start() {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }
    setStatus('active');
    setSearching(true);
    setLastEvent('starter…');
    void queryPermission();
    void enableWakeLock().then((ok) => setWakeLockOn(ok && isWakeLockActive()));

    navigator.geolocation.getCurrentPosition(
      (pos) => handle(pos),
      (err) => setLastEvent(`grov: ${errLabel(err.code)}`),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => handle(pos),
      (err) => {
        setLastEvent(`GPS: ${errLabel(err.code)}`);
        if (err.code === 1) {
          if (watchId.current !== null) {
            navigator.geolocation.clearWatch(watchId.current);
            watchId.current = null;
          }
          void disableWakeLock();
          setWakeLockOn(false);
          setStatus('denied');
          setSearching(false);
          return;
        }
        setSearching(true);
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 30_000 },
    );

    pollId.current = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => handle(pos),
        (err) => setLastEvent(`poll: ${errLabel(err.code)}`),
        { enableHighAccuracy: true, timeout: 27_000, maximumAge: 1000 },
      );
    }, 3000);
  }

  useEffect(() => {
    void queryPermission();
    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      if (pollId.current !== null) clearInterval(pollId.current);
      void disableWakeLock();
    };
  }, []);

  const goodFix =
    status === 'active' && fix != null && fix.accuracy != null && fix.accuracy <= GOOD_ACCURACY_M;

  return { status, fix, goodFix, searching, lastEvent, permState, wakeLockOn, start, stop };
}
